import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { errMsg, formatDate, formatRp, normalizeWa, waLink } from "@/lib/api";
import { StatusBadge } from "@/components/shop/StatusBadge";
import { OrderSummary } from "@/pages/Payment";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const FILTERS = ["", "pending", "paid", "packed", "shipped", "completed", "expired", "cancelled"];
const NEXT = { paid: "packed", packed: "shipped", shipped: "completed" };
const NEXT_LABEL = { packed: "Tandai Dikemas", shipped: "Tandai Dikirim", completed: "Tandai Selesai" };

const OrderDetail = ({ order, onChange }) => {
  const call = useApi();
  const [resi, setResi] = useState(order.tracking_number || "");
  const update = async (body) => {
    try { onChange(await call("patch", `/admin/orders/${order.order_id}`, body)); toast.success("Pesanan diperbarui"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const next = NEXT[order.status];
  const buyerMsg = `Halo ${order.sender.name}, update pesanan ${order.order_id} di EllMom Handmade: status ${order.status}${order.tracking_number ? `, no. resi ${order.tracking_number}` : ""}.`;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><StatusBadge status={order.status} testId="order-detail-status" /><span className="text-xs text-stone-500">{formatDate(order.created_at)}</span></div>
      <div className="rounded-lg bg-stone-50 p-3 text-sm">
        <p className="font-medium">Pemesan: {order.sender.name}</p>
        <p className="text-stone-600">{order.sender.phone} {order.sender.email && `· ${order.sender.email}`}</p>
        {order.note && <p className="mt-2 text-stone-600">Catatan: {order.note}</p>}
        <p className="mt-2 text-xs text-stone-500">HPP: {formatRp(order.hpp_total)} · Laba: {formatRp(order.subtotal - order.hpp_total)} · Bayar via: {order.payment?.paid_via || order.payment?.mode}</p>
      </div>
      <div className="flex gap-2">
        <Input placeholder="No. resi pengiriman" value={resi} onChange={(e) => setResi(e.target.value)} data-testid="order-resi-input" />
        <Button variant="outline" data-testid="order-resi-save" onClick={() => update({ tracking_number: resi })}>Simpan</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {next && <Button data-testid="order-next-status-button" onClick={() => update({ status: next })}>{NEXT_LABEL[next]}</Button>}
        {["pending", "expired"].includes(order.status) && <Button variant="outline" data-testid="order-mark-paid-button" onClick={() => update({ status: "paid" })}>Tandai Lunas Manual</Button>}
        {!["completed", "cancelled"].includes(order.status) && <Button variant="ghost" className="text-red-700" data-testid="order-cancel-button" onClick={() => update({ status: "cancelled" })}>Batalkan</Button>}
        <a href={waLink(buyerMsg, normalizeWa(order.sender.phone))} target="_blank" rel="noreferrer" data-testid="order-wa-buyer"
          className="inline-flex h-9 items-center rounded-md border border-green-600 px-3 text-sm text-green-700 hover:bg-green-50"><MessageCircle className="mr-1.5 h-4 w-4" /> WA Pembeli</a>
      </div>
      <OrderSummary o={order} />
    </div>
  );
};

export default function AdminOrders() {
  const call = useApi();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => call("get", "/admin/orders", undefined, { params: { status: status || undefined, q: q || undefined } }).then(setOrders), [call, status, q]);
  useEffect(() => { load(); }, [load]);

  const onChange = (o) => { setSel(o); load(); };

  return (
    <div>
      <PageHeader title="Pesanan" />
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
        <Input placeholder="Cari no. pesanan / nama / HP" className="max-w-sm bg-white" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-order-search" />
        <div className="flex gap-1.5 overflow-x-auto">
          {FILTERS.map((s) => (
            <button key={s} data-testid={`order-filter-${s || "all"}`} onClick={() => setParams(s ? { status: s } : {})}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs ${status === s ? "border-amber-700 bg-amber-50 text-amber-900" : "border-stone-200 bg-white text-stone-600"}`}>{s ? s[0].toUpperCase() + s.slice(1) : "Semua"}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-stone-100 text-left text-xs text-stone-600"><tr><th className="p-3">No. Pesanan</th><th className="p-3">Tanggal</th><th className="p-3">Pemesan</th><th className="p-3">Tujuan</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th></tr></thead>
          <tbody className="divide-y divide-stone-100">
            {(orders || []).map((o) => (
              <tr key={o.order_id} onClick={() => setSel(o)} className="cursor-pointer hover:bg-stone-50" data-testid={`admin-order-row-${o.order_id}`}>
                <td className="p-3 font-medium whitespace-nowrap">{o.order_id}</td>
                <td className="p-3 whitespace-nowrap text-stone-600">{formatDate(o.created_at)}</td>
                <td className="p-3">{o.sender.name}<span className="block text-xs text-stone-500">{o.sender.phone}</span></td>
                <td className="p-3 text-stone-600">{o.recipient.city}, {o.recipient.province}<span className="block text-xs">{o.courier.name} {o.courier.service}</span></td>
                <td className="tnum p-3 text-right">{formatRp(o.total)}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders?.length === 0 && <p className="p-6 text-center text-sm text-stone-500" data-testid="admin-orders-empty">Belum ada pesanan dengan status ini.</p>}
      </div>
      <Sheet open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="order-detail-sheet">
          <SheetHeader><SheetTitle>{sel?.order_id}</SheetTitle></SheetHeader>
          {sel && <div className="mt-4"><OrderDetail key={sel.order_id + sel.status} order={sel} onChange={onChange} /></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
